import { Component, OnInit } from '@angular/core';
import { AppService } from 'app/services/app/app.service';
import { RestService } from 'app/services/rest/rest.service';

type JsonPrimitive = string | number | boolean | null;
type JsonValue = JsonPrimitive | JsonValue[] | { [key: string]: JsonValue };

interface JsonPathNode {
  label: string;
  value: JsonValue | unknown;
}

interface JsonRootEntry {
  key: string;
  value: JsonValue | unknown;
}

interface JsonObjectEntry {
  key: string;
  value: JsonValue | unknown;
}

interface JsonArrayValueEntry {
  index: number;
  value: JsonValue | unknown;
}

interface JsonArrayObjectRow {
  __index: number;
  __raw: Record<string, unknown>;
  [key: string]: unknown;
}

@Component({
  selector: 'app-admin-kiosk',
  templateUrl: './admin-kiosk.page.html',
  styleUrls: ['./admin-kiosk.page.scss'],
})
export class AdminKioskPage implements OnInit {
  kioskDb: Record<string, unknown> = {};
  loading = false;
  errorMessage = '';
  path: JsonPathNode[] = [];

  constructor(    
    private restService: RestService,
    public appService: AppService) { }

  ngOnInit(): void {
    this.loadKioskDb();
  }

  loadKioskDb(): void {
    this.loading = true;
    this.errorMessage = '';

    this.restService.getAdminKioskDb().subscribe({
      next: (result) => {
        this.kioskDb = this.extractRoot(result);
        this.path = [];
        this.loading = false;
      },
      error: () => {
        this.kioskDb = {};
        this.path = [];
        this.errorMessage = 'No se pudo cargar la base de datos del kiosk.';
        this.loading = false;
      }
    });
  }

  rootEntries(): JsonRootEntry[] {
    return Object.keys(this.kioskDb || {}).map((key) => ({
      key,
      value: this.kioskDb[key]
    }));
  }

  currentNode(): unknown {
    return this.path.length ? this.path[this.path.length - 1].value : this.kioskDb;
  }

  isRootNode(): boolean {
    return this.path.length === 0;
  }

  openRoot(entry: JsonRootEntry): void {
    this.path = [{ label: entry.key, value: entry.value }];
  }

  goToRoot(): void {
    this.path = [];
  }

  goToPath(index: number): void {
    this.path = this.path.slice(0, index + 1);
  }

  currentNodeKind(): 'root' | 'object' | 'array-objects' | 'array-values' | 'primitive' {
    const node = this.currentNode();

    if (this.isRootNode()) {
      return 'root';
    }

    if (Array.isArray(node)) {
      return this.isArrayOfObjects(node) ? 'array-objects' : 'array-values';
    }

    if (this.isPlainObject(node)) {
      return 'object';
    }

    return 'primitive';
  }

  currentNodeTypeLabel(): string {
    const node = this.currentNode();

    if (Array.isArray(node)) {
      return 'Array';
    }

    if (this.isPlainObject(node)) {
      return 'Objeto';
    }

    return 'Valor';
  }

  currentNodeCountLabel(): string {
    const node = this.currentNode();

    if (Array.isArray(node)) {
      return `${node.length} elementos`;
    }

    if (this.isPlainObject(node)) {
      return `${Object.keys(node).length} propiedades`;
    }

    return '';
  }

  objectEntries(): JsonObjectEntry[] {
    const node = this.currentNode();

    if (!this.isPlainObject(node)) {
      return [];
    }

    return Object.keys(node).map((key) => ({
      key,
      value: node[key]
    }));
  }

  arrayValueEntries(): JsonArrayValueEntry[] {
    const node = this.currentNode();

    if (!Array.isArray(node) || this.isArrayOfObjects(node)) {
      return [];
    }

    return node.map((value, index) => ({ index, value }));
  }

  arrayObjectRows(): JsonArrayObjectRow[] {
    const node = this.currentNode();

    if (!Array.isArray(node) || !this.isArrayOfObjects(node)) {
      return [];
    }

    return node.map((item, index) => ({
      __index: index,
      __raw: item,
      ...item
    }));
  }

  tableColumns(): string[] {
    const rows = this.arrayObjectRows();
    const columns = new Set<string>();

    rows.forEach((row) => {
      Object.keys(row.__raw || {}).forEach((key) => columns.add(key));
    });

    return ['__index', ...Array.from(columns)];
  }

  openObjectEntry(entry: JsonObjectEntry): void {
    if (!this.isExpandable(entry.value)) {
      return;
    }

    this.path = [...this.path, { label: entry.key, value: entry.value }];
  }

  openArrayValueEntry(entry: JsonArrayValueEntry): void {
    if (!this.isExpandable(entry.value)) {
      return;
    }

    this.path = [...this.path, { label: `[${entry.index}]`, value: entry.value }];
  }

  openArrayObjectRow(row: JsonArrayObjectRow): void {
    this.path = [...this.path, { label: `[${row.__index}]`, value: row.__raw }];
  }

  openArrayObjectCell(row: JsonArrayObjectRow, column: string, event: Event): void {
    event.stopPropagation();

    const value = row[column];
    if (!this.isExpandable(value)) {
      return;
    }

    this.path = [
      ...this.path,
      { label: `[${row.__index}]`, value: row.__raw },
      { label: column, value }
    ];
  }

  isExpandable(value: unknown): boolean {
    return Array.isArray(value) || this.isPlainObject(value);
  }

  expandLabel(value: unknown): string {
    if (Array.isArray(value)) {
      return `[${value.length}]`;
    }

    if (this.isPlainObject(value)) {
      return `{${Object.keys(value).length}}`;
    }

    return '';
  }

  formatPrimitive(value: unknown): string {
    if (value === null) {
      return 'null';
    }

    if (value === undefined) {
      return 'undefined';
    }

    if (typeof value === 'boolean') {
      return value ? 'true' : 'false';
    }

    if (typeof value === 'number') {
      const timestampDate = this.tryParseTimestamp(`${value}`);
      if (timestampDate) {
        return timestampDate.toLocaleString('es-ES');
      }

      return `${value}`;
    }

    if (typeof value !== 'string') {
      return String(value);
    }

    if (!value) {
      return '""';
    }

    const timestampDate = this.tryParseTimestamp(value);
    if (timestampDate) {
      return timestampDate.toLocaleString('es-ES');
    }

    const isoDate = new Date(value);
    if (!Number.isNaN(isoDate.getTime()) && (value.includes('T') || value.includes('-'))) {
      return isoDate.toLocaleString('es-ES');
    }

    return value;
  }

  hasVisibleRootData(): boolean {
    return this.rootEntries().length > 0;
  }

  private isArrayOfObjects(value: unknown[]): value is Record<string, unknown>[] {
    return value.every((item) => this.isPlainObject(item));
  }

  private isPlainObject(value: unknown): value is Record<string, unknown> {
    return value !== null && typeof value === 'object' && !Array.isArray(value);
  }

  private tryParseTimestamp(value: string): Date | null {
    if (!/^\d{13}$/.test(value)) {
      return null;
    }

    const parsed = new Date(Number(value));
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  private extractRoot(result: unknown): Record<string, unknown> {
    if (this.isPlainObject(result)) {
      return result;
    }

    return {};
  }
}
